// Copyright (c) 2025 Juan Beltrán
// Licensed under the MIT License. See LICENSE file for details.

import type { APIConfig, Message, ChatContext, AIModel, APIError, MessageContent } from '../types/models';
import { enhanceSystemPrompt } from './codeFormatting';
import { sendMessage as sendAnthropicMessage } from './providers/anthropic/client';
import { sendXaiMessage } from './providers/xai';
import { sendOpenAIMessage } from './providers/openai';
import { sendMessage as sendGroqMessage } from './providers/groq/client';
import { sendMessage as sendGeminiMessage } from './providers/gemini/client';
import { sendMessage as sendDeepseekMessage } from './providers/deepseek/client';
import { sendMessage as sendCerebrasMessage } from './providers/cerebras/client';
import { sendMessage as sendMinimaxMessage } from './providers/minimax/client';
import { QwenClient } from './providers/qwen/client';
import { QwenModelId } from './providers/qwen/constants';
import { useStore } from '../store/useStore';
import { determineApiUsage } from './apiUsageManager';
import { MODELS_DATABASE } from '../config/modelsDatabase';
import { usageService } from '../services/UsageService';
import { sendMessage as sendMistralMessage } from './providers/mistral/client';

import type { ProviderMessage, MessageContent } from '../types/models';

export async function sendMessage(
  message: MessageContent,
  config: APIConfig,
  modelId: string,
  context: ChatContext,
  previousMessages: Message[],
  fileData?: { mime_type: string; data: string }
): Promise<string> {

  // Helper function to get text content from message
  const getTextContent = (msg: MessageContent): string => {
    if (typeof msg === 'string') return msg;
    return msg.find(c => c.type === 'text')?.text || '';
  };

  const { user, topUpValue, apiConfigs } = useStore.getState();
  if (!user) {
    throw new Error("User not authenticated");
  }

  // Enhance context with code formatting instructions
  context = {
    ...context,
    systemPrompt: enhanceSystemPrompt(context.systemPrompt)
  };

  // Get model information from database
  const model = MODELS_DATABASE[modelId];
  if (!model) {
    throw new Error(`No model configuration found for model: ${modelId}`);
  }

  // Calculate total message length including context and previous messages
  const totalPromptLength = [
    message,
    ...previousMessages.map(m => m.content || ''),
    context.systemPrompt || ''
  ].join(' ').length;

  // More accurate token estimation
  const estimatedPromptTokens = Math.ceil(totalPromptLength / 4);
  const estimatedCompletionTokens = Math.ceil(estimatedPromptTokens * 0.5);
  
  const estimatedCost = 
    (estimatedPromptTokens / 1000) * model.pricing.prompt_tokens_per_1k +
    (estimatedCompletionTokens / 1000) * model.pricing.completion_tokens_per_1k;

  console.log('Cost estimation:', {
    promptTokens: estimatedPromptTokens,
    completionTokens: estimatedCompletionTokens,
    promptCost: (estimatedPromptTokens / 1000) * model.pricing.prompt_tokens_per_1k,
    completionCost: (estimatedCompletionTokens / 1000) * model.pricing.completion_tokens_per_1k,
    totalCost: estimatedCost,
    currentBalance: topUpValue
  });

  const modelInfo: AIModel = {
    ...model,
    isConfigured: true,
    systemApiCost: estimatedCost
  };

  try {
    // Check if user has API key configured for this provider
    const hasUserApiKey = apiConfigs.some(
      config => config.provider === modelInfo.provider && config.apiKey
    );

    // Determine which API to use
    const apiUsageResult = determineApiUsage(topUpValue, hasUserApiKey, modelInfo);
    console.log('API usage decision:', apiUsageResult);

    if (apiUsageResult.action === 'showErrorMessage') {
      const error: APIError = {
        error: 'api_usage_error',
        message: apiUsageResult.message || 'API usage error',
        code: 'insufficient_funds'
      };
      throw error;
    }

    // Update config based on API usage decision
    if (apiUsageResult.action === 'useSystemApi') {
      if (!apiUsageResult.systemApiKey) {
        throw new Error(`No system API key available for ${config.provider}`);
      }
      
      // Using proxy approach - don't expose actual API keys in client
      config = {
        ...config,
        // Use a token instead of the actual API key
        apiKey: apiUsageResult.systemApiKey,
        isSystemApi: true,
        // Flag to indicate we're using system API via proxy
        useProxy: true
      };

      // Update balance before API call
      if (apiUsageResult.updatedTopUpValue !== undefined) {
        console.log('Updating balance:', {
          previous: topUpValue,
          new: apiUsageResult.updatedTopUpValue,
          cost: estimatedCost
        });

        await usageService.updateBalance(user.uid, apiUsageResult.updatedTopUpValue);
      }
    }

    // Ensure API key is available
    if (!config.apiKey) {
      const error: APIError = {
        error: 'api_not_configured',
        message: "API key is required. Please configure your API key in the settings.",
        code: 'api_not_configured'
      };
      throw error;
    }

    let response: string;

    switch (config.provider) {
      case "anthropic":
        response = await sendAnthropicMessage(
          message,
          config.apiKey,
          modelId,
          context,
          previousMessages
        );
        break;
      case "xai":
        response = await sendXaiMessage(
          message,
          config,
          modelId,
          context,
          previousMessages
        );
        break;
      case "groq":
        response = await sendGroqMessage(
          message,
          config.apiKey,
          modelId,
          context,
          previousMessages
        );
        break;
      case "gemini":
        response = await sendGeminiMessage(
          message,
          config.apiKey,
          modelId,
          context,
          previousMessages,
          fileData
        );
        break;
      case "openai":
        response = await sendOpenAIMessage(
          message,
          config,
          modelId,
          context,
          previousMessages,
          user.uid,
          topUpValue
        );
        break;
      case "deepseek":
        response = await sendDeepseekMessage(
          message,
          config,
          modelId,
          context,
          previousMessages
        );
        break;
      case "cerebras":
        response = await sendCerebrasMessage(
          message,
          config.apiKey,
          modelId,
          context,
          previousMessages
        );
        break;
      case "minimax":
        response = await sendMinimaxMessage(
          message,
          config.apiKey,
          modelId,
          context,
          previousMessages,
          user.uid,
          topUpValue
        );
        break;
      case "qwen":
        const qwenClient = new QwenClient(config.apiKey);
        response = await qwenClient.chat(
          [
            {
              id: Date.now().toString(),
              role: 'system',
              content: context.systemPrompt || '',
              timestamp: Date.now(),
              modelId
            },
            ...previousMessages,
            {
              id: (Date.now() + 1).toString(),
              role: 'user',
              content: message,
              timestamp: Date.now(),
              modelId
            }
          ],
          modelId as QwenModelId
        );
        break;
      case "mistral":
        response = await sendMistralMessage(
          message,
          config.apiKey,
          modelId,
          context,
          previousMessages
        );
        break;
      default:
        throw new Error(`Unsupported provider: ${config.provider}`);
    }

    // Verify the balance update
    const currentState = useStore.getState();
    console.log('Final balance check:', {
      balance: currentState.topUpValue,
      expected: apiUsageResult.action === 'useSystemApi' ? apiUsageResult.updatedTopUpValue : topUpValue,
      cost: estimatedCost
    });

    return response;
  } catch (error) {
    console.error("Error sending message:", error);
    
    if ((error as APIError).code) {
      throw error;
    }
    
    throw error instanceof Error
      ? error
      : new Error(
          `Failed to communicate with ${config.provider.toUpperCase()} API`
        );
  }
}